import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { setUser as sentrySetUser } from "@/lib/sentry-lazy";
import { BrandedLoader } from "@/components/BrandedLoader";
import { prewarmTranscribe } from "@/lib/prewarmTranscribe";
import { prefetchHomeData } from "@/hooks/useHomeData";
import { createLogger } from '@/lib/logger';
const log = createLogger('ProtectedRoute');

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const resolvedRef = useRef(false);
  const queryClient = useQueryClient();

  const resolveAuth = (isAuthenticated: boolean, onboarding: boolean) => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    setAuthenticated(isAuthenticated);
    setNeedsOnboarding(onboarding);
    setLoading(false);
  };

  const fetchProfileAndResolve = async (userId: string) => {
    try {
      // Brand-new OAuth users may hit this before the create-profile trigger
      // commits. Retry up to 3× with a short backoff so we don't flash Home.
      let profile: { onboarding_completed?: boolean; onboarding_paused?: boolean } | null = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        const { data, error } = await supabase
          .from("profiles")
          .select("onboarding_completed, onboarding_paused")
          .eq("id", userId)
          .maybeSingle();
        if (error) log.warn("Error fetching profile:", error);
        if (data) { profile = data as typeof profile; break; }
        await new Promise(r => setTimeout(r, 400));
      }
      const completed = !!profile?.onboarding_completed;
      const paused = !!profile?.onboarding_paused;
      const onboarding = !completed && !paused;
      resolveAuth(true, onboarding);
      prewarmTranscribe();
      void prefetchHomeData(queryClient);
    } catch (error) {
      log.error("Profile fetch error:", error);
      resolveAuth(true, false);
      prewarmTranscribe();
      void prefetchHomeData(queryClient);
    }
  };

  useEffect(() => {
    // onAuthStateChange is the single source of truth.
    // It fires INITIAL_SESSION on mount (with or without a session),
    // then SIGNED_IN / SIGNED_OUT / TOKEN_REFRESHED as needed.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        sentrySetUser(null);
        resolveAuth(false, false);
        return;
      }

      // INITIAL_SESSION resolves the loading state definitively
      if (event === 'INITIAL_SESSION') {
        if (session?.user) {
          sentrySetUser({ id: session.user.id });
          await fetchProfileAndResolve(session.user.id);
        } else {
          sentrySetUser(null);
          resolveAuth(false, false);
        }
        return;
      }

      // Handle subsequent sign-ins (e.g. OAuth redirect completing)
      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session?.user) {
        sentrySetUser({ id: session.user.id });
        if (!resolvedRef.current) {
          await fetchProfileAndResolve(session.user.id);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return <BrandedLoader />;
  }

  if (!authenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (needsOnboarding) {
    return <Navigate to="/app/onboarding" replace />;
  }

  return <>{children}</>;
}

export function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'INITIAL_SESSION') {
        if (session?.user) {
          setAuthenticated(true);
          try {
            const { data: profile, error } = await supabase
              .from("profiles")
              .select("onboarding_completed")
              .eq("id", session.user.id)
              .maybeSingle();

            if (error) log.warn("Error fetching profile:", error);
            // Only force-exit when fully completed. Paused users may explicitly
            // visit /app/onboarding to resume — let them in.
            setOnboardingCompleted(!!profile?.onboarding_completed);
          } catch (error) {
            log.error("Onboarding auth check error:", error);
          }
        }
        setLoading(false);
        return;
      }

      if (event === 'SIGNED_OUT') {
        setAuthenticated(false);
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return <BrandedLoader />;
  }

  if (!authenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (onboardingCompleted) {
    return <Navigate to="/app/home" replace />;
  }

  return <>{children}</>;
}